import * as React from "react";
import { cn } from "@/lib/utils";
import "./style.scss";
import { Card as CardT } from "@/payload-types";
import { Relationship, resolveRelationshipId } from "@/lib/utils/theme-configuration-class";

export type CardVariant = Relationship<CardT>;

export interface CardProps {
	children: React.ReactNode;
	className?: string;
	variant?: CardVariant;
	hover?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
	({ children, className, variant = "default", hover = false, ...props }, ref) => {
		return (
			<div
				ref={ref}
				className={cn(
					"flex flex-col box-border w-full overflow-hidden transition",
					"ms-card ms-card-" + resolveRelationshipId(variant),
					hover && "ms-card-with-hover",
					className,
				)}
				{...props}
			>
				{children}
			</div>
		);
	},
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => {
	return <div ref={ref} className={cn("flex flex-col gap-2", "ms-card-header", className)} {...props} />;
});
CardHeader.displayName = "CardHeader";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => {
	return <div ref={ref} className={cn("flex flex-col gap-3 grow", "ms-card-content", className)} {...props} />;
});
CardContent.displayName = "CardContent";

const CardMeta = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => {
	return <div ref={ref} className={cn("flex flex-col gap-1", "ms-card-meta", className)} {...props} />;
});
CardMeta.displayName = "CardMeta";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => {
	return <div ref={ref} className={cn("flex gap-2", "ms-card-footer", className)} {...props} />;
});
CardFooter.displayName = "CardFooter";

interface CardImageProps extends React.HTMLAttributes<HTMLElement> {
	children: React.ReactNode;
	wrap?: "16-9" | "1-1" | "4-3" | "2-3" | string;
}

const CardFigure = React.forwardRef<HTMLElement, CardImageProps>(({ children, wrap = "16-9", className, ...props }, ref) => {
	return (
		<figure ref={ref} className={cn("ms-media-wrap", `ms-media-wrap-${wrap}`, className)} {...props}>
			{children}
		</figure>
	);
});
CardFigure.displayName = "CardImage";

export { Card, CardHeader, CardContent, CardMeta, CardFooter, CardFigure };

export default Card;
